import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressRing } from "@/components/ui/progress-ring";
import { cn } from "@/lib/utils/cn";

import { Badge } from "./ui/Badge";

import {
  useActionPlans,
  useCreateActionPlan,
  useDeleteActionPlan,
} from "../api/hooks";
import type { ActionPlanListItem } from "../api/types";
import { apiErrorDetail } from "../lib/errors";

/** Four-theme spectrum, matching the shell identity strip. */
const SPECTRUM =
  "linear-gradient(90deg, hsl(var(--theme-governance)), hsl(var(--theme-economic)), hsl(var(--theme-social)), hsl(var(--theme-environment)))";

function Stat({
  value,
  label,
  className,
  cellClassName,
}: {
  value: number;
  label: string;
  className?: string;
  cellClassName?: string;
}) {
  return (
    <div className={cn("px-2 py-2.5 text-center", cellClassName)}>
      <div className={cn("text-lg font-extrabold tabular-nums leading-none", className)}>
        {value}
      </div>
      <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  onOpen,
  onDelete,
  deleting,
}: {
  plan: ActionPlanListItem;
  onOpen: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t, i18n } = useTranslation();
  const total = Number(plan.action_count);
  const done = Number(plan.completed_action_count);
  const inProgress = Number(plan.in_progress_action_count);
  const notDone = Number(plan.not_done_action_count);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const period = [plan.time_planned_startyear, plan.time_planned_endyear]
    .filter((y) => y != null)
    .join(" – ");
  const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language) : "—";

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-md">
      <div aria-hidden className="h-1.5" style={{ background: SPECTRUM }} />
      <div className="p-5">
        <div className="flex items-start gap-4">
          <ProgressRing
            value={pct}
            size={72}
            strokeWidth={8}
            spectrum={!plan.is_completed}
            indicatorClassName={plan.is_completed ? "stroke-success" : undefined}
          >
            <span className="text-sm font-extrabold tabular-nums">{pct}%</span>
          </ProgressRing>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-lg font-bold tracking-tight">
                {plan.name || t("actionPlans.list.untitled")}
              </h3>
              {!plan.is_completed && (
                <Badge tone="neutral">{t("actionPlans.list.draft")}</Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("actionPlans.list.created", { date: fmtDate(plan.created_at) })}
              {" · "}
              {t("actionPlans.list.updated", { date: fmtDate(plan.modified_at) })}
              {period && ` · ${period}`}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 rounded-lg border sm:grid-cols-4">
          <Stat
            value={total}
            label={t("actionPlans.list.total")}
            cellClassName="border-b border-r sm:border-b-0"
          />
          <Stat
            value={done}
            label={t("actionPlans.status.done")}
            className="text-success"
            cellClassName="border-b sm:border-b-0 sm:border-r"
          />
          <Stat
            value={inProgress}
            label={t("actionPlans.status.in_progress")}
            className="text-theme-economic-ink"
            cellClassName="border-r"
          />
          <Stat
            value={notDone}
            label={t("actionPlans.status.not_done")}
            className="text-muted-foreground"
          />
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button size="sm" onClick={onOpen}>
            {t("actionPlans.list.open")}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("actionPlans.list.delete")}
            onClick={onDelete}
            disabled={deleting}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Landing view: the enterprise's action plans, plus create/open/delete. */
export function PlanList({ onOpen }: { onOpen: (planId: string) => void }) {
  const { t } = useTranslation();
  const plans = useActionPlans();
  const createPlan = useCreateActionPlan();
  const deletePlan = useDeleteActionPlan();

  const create = () => {
    createPlan.mutate(
      {},
      {
        onSuccess: (created) => onOpen(created.id),
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
      },
    );
  };

  const remove = (id: string) => {
    if (!window.confirm(t("actionPlans.list.confirmDelete"))) return;
    deletePlan.mutate(id, {
      onError: (err) =>
        toast.error(apiErrorDetail(err) ?? t("actionPlans.errors.saveFailed")),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("actionPlans.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("actionPlans.subtitle")}</p>
        </div>
        <Button onClick={create} disabled={createPlan.isPending}>
          <Plus />
          {createPlan.isPending
            ? t("actionPlans.list.creating")
            : t("actionPlans.list.newPlan")}
        </Button>
      </div>

      {plans.isLoading && (
        <p className="text-sm text-muted-foreground">{t("actionPlans.loading")}</p>
      )}

      {plans.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("actionPlans.errors.loadFailed")}</p>
            <Button variant="outline" size="sm" onClick={() => void plans.refetch()}>
              {t("actionPlans.errors.retry")}
            </Button>
          </CardContent>
        </Card>
      )}

      {plans.data && plans.data.length === 0 && (
        <Card>
          <CardContent className="space-y-1 py-6">
            <p className="font-medium">{t("actionPlans.list.empty")}</p>
            <p className="text-sm text-muted-foreground">{t("actionPlans.list.emptyHint")}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {plans.data?.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onOpen={() => onOpen(plan.id)}
            onDelete={() => remove(plan.id)}
            deleting={deletePlan.isPending}
          />
        ))}
      </div>
    </div>
  );
}
